"use client"

import { useCallback, useMemo } from "react"
import { useEditorStore } from "@/lib/editor-store"
import type { editor as MonacoEditor } from "monaco-editor"
import * as latex from "@/lib/ribbon/latex-actions"

export function useRibbonActions() {
  const {
    runOnActiveEditor,
    getActiveMonacoEditor,
    undoActiveEditor,
    redoActiveEditor,
  } = useEditorStore()

  const run = useCallback(
    (fn: (ed: MonacoEditor.IStandaloneCodeEditor) => void) => {
      runOnActiveEditor(fn)
    },
    [runOnActiveEditor],
  )

  return useMemo(
    () => ({
      getActiveMonacoEditor,
      cut: () => void run((ed) => latex.clipboardAction(ed, "cut")),
      copy: () => void run((ed) => latex.clipboardAction(ed, "copy")),
      paste: () => void run((ed) => latex.clipboardAction(ed, "paste")),
      undo: () => undoActiveEditor(),
      redo: () => redoActiveEditor(),
      findInDocument: (query: string, direction?: "next" | "prev") =>
        run((ed) => latex.findInDocument(ed, query, direction ?? "next")),
      bold: () => run(latex.applyBold),
      italic: () => run(latex.applyItalic),
      underline: () => run(latex.applyUnderline),
      alignLeft: () => run((ed) => latex.applyAlign(ed, "left")),
      alignCenter: () => run((ed) => latex.applyAlign(ed, "center")),
      alignRight: () => run((ed) => latex.applyAlign(ed, "right")),
      justify: () => run((ed) => latex.applyAlign(ed, "justify")),
      bulletList: () => run((ed) => latex.insertList(ed, "itemize")),
      numberList: () => run((ed) => latex.insertList(ed, "enumerate")),
      alphaList: () => run((ed) => latex.insertList(ed, "alpha")),
      increaseIndent: () => run(latex.increaseIndent),
      decreaseIndent: () => run(latex.decreaseIndent),
      fontFamily: (name: string) => run((ed) => latex.applyFontFamily(ed, name)),
      fontSize: (size: number) => run((ed) => latex.applyFontSize(ed, size)),
      fillColor: (color: string) => run((ed) => latex.applyFillColor(ed, color)),
      textColor: (color: string) => run((ed) => latex.applyTextColor(ed, color)),
      pageBreak: () => run(latex.insertPageBreak),
      picture: () => run(latex.insertPicture),
      shapes: (shapeId?: string) =>
        run((ed) => latex.insertShapes(ed, shapeId ?? "rectangle")),
      equation: (body?: string) =>
        run((ed) => latex.insertEquation(ed, body)),
      getEquationDraft: () =>
        latex.getEquationDraftFromEditor(getActiveMonacoEditor()),
      codeBlock: () => run(latex.insertCodeBlock),
      emoji: (e: string) => run((ed) => latex.insertEmoji(ed, e)),
      table: () => run((ed) => latex.insertTable(ed)),
      video: () => run(latex.insertVideo),
      link: () => run(latex.insertLink),
      bookmark: () => run(latex.insertBookmark),
      comment: () => run(latex.insertComment),
      header: () => run(latex.insertHeader),
      footer: () => run(latex.insertFooter),
      pageNumber: () => run(latex.insertPageNumber),
      textBox: () => run(latex.insertTextBox),
      dateAndTime: () => run(latex.insertDateAndTime),
      dropCap: () => run(latex.insertDropCap),
      draw: () => run(latex.insertDraw),
      documentFormatting: () => run(latex.insertDocumentFormatting),
      themeColor: (color?: string) =>
        run((ed) => latex.insertThemeColor(ed, color ?? "#2563EB")),
      fontTheme: (name?: string) => run((ed) => latex.insertFontTheme(ed, name)),
      effects: () => run(latex.insertEffects),
      watermark: () => run(latex.insertWatermark),
      pageColor: (color: string) => run((ed) => latex.applyPageColor(ed, color)),
      pageEffects: () => run(latex.insertPageEffects),
      pageBorder: () => run(latex.insertPageBorder),
      // Layout
      margins: () => run(latex.insertMargins),
      marginSide: (side: "left" | "right" | "top" | "bottom", px: number) =>
        run((ed) => latex.applyMarginSide(ed, side, px)),
      orientation: (landscape?: boolean) =>
        run((ed) => latex.insertOrientation(ed, landscape ?? true)),
      pageSize: () => run(latex.insertPageSize),
      columns: (count?: number) => run((ed) => latex.insertColumns(ed, count ?? 2)),
      breaks: () => run(latex.insertBreaks),
      lineNumbers: () => run(latex.insertLineNumbers),
      position: () => run(latex.insertPosition),
      selectPanel: () => run(latex.insertSelectPanel),
      wrapText: () => run(latex.insertWrapText),
      bringForward: () => run(latex.insertBringForward),
      bringBackward: () => run(latex.insertBringBackward),
      objectAlign: () => run(latex.insertObjectAlign),
      // Mailings
      envelopes: () => run(latex.insertEnvelopes),
      labels: () => run(latex.insertLabels),
      mailMerge: () => run(latex.insertMailMerge),
      selectRecipients: () => run(latex.insertSelectRecipients),
      editRecipients: () => run(latex.insertEditRecipients),
      editRecipientList: () => run(latex.insertEditRecipients),
      highlightFields: () => run(latex.insertHighlightFields),
      addressBlock: () => run(latex.insertAddressBlock),
      greetingLine: () => run(latex.insertGreetingLine),
      insertMergeField: () => run(latex.insertMergeField),
      matchFields: () => run(latex.insertMatchFields),
      rules: () => run(latex.insertRules),
      updateLabel: () => run(latex.insertUpdateLabel),
      previewResults: () => run(latex.insertPreviewResults),
      checkForErrors: () => run(latex.insertCheckForErrors),
      findRecipient: (q: string) => run((ed) => latex.findRecipientInEditor(ed, q)),
    }),
    [getActiveMonacoEditor, run, undoActiveEditor, redoActiveEditor],
  )
}
